import { Component, Input, OnInit, AfterViewInit, ElementRef, ViewChild, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';

import { MapsService } from '../../../../../core/service/google/maps/maps.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'modal-view-map',
  standalone: true,
  imports: [],
  template: `
    <div class="relative w-full h-48 sm:h-64 rounded-lg overflow-hidden shadow-sm bg-gray-50">
      <!-- Contenedor del mapa -->
      <div #mapContainer class="w-full h-full"></div>
    
      <!-- Indicador de carga -->
      @if (!mapLoaded) {
        <div class="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-70">
          <div class="flex flex-col items-center">
            <div class="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-b-2 border-green-500 mb-2"></div>
            <p class="text-xs sm:text-sm text-gray-700">Cargando mapa...</p>
          </div>
        </div>
      }
    </div>
    `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `]
})
export class MapViewComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  @Input() initialLocation: { lat: number, lng: number } = { lat: 19.4326, lng: -99.1332 }; // Ciudad de México por defecto
  @Input() draggableMarker: boolean = true; // Permitir arrastrar el marcador por defecto

  // Evento para emitir la ubicación y dirección seleccionada al arrastrar el marcador
  @Output() locationChanged = new EventEmitter<{
    location: { lat: number, lng: number },
    address: string
  }>();

  private map: google.maps.Map | null = null;
  private marker: google.maps.Marker | null = null;
  private scriptLoadedSubscription?: Subscription;
  mapLoaded = false;

  constructor(private mapsService: MapsService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Suscribirse al estado de carga del script de Google Maps
    this.scriptLoadedSubscription = this.mapsService.scriptLoaded$.subscribe(loaded => {
      if (loaded) {
        // El script está cargado, pero esperamos a que el componente esté en el DOM
        setTimeout(() => {
          if (this.mapContainer) {
            this.initializeMap();
          }
        }, 100);
      }
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    // Inicializar el mapa cuando la vista esté lista
    // Añadimos un pequeño retraso para asegurar que el contenedor esté completamente renderizado
    setTimeout(() => {
      this.initializeMap();
    }, 100);
  }

  ngOnDestroy(): void {
    // Limpiar suscripciones
    if (this.scriptLoadedSubscription) {
      this.scriptLoadedSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cambios en la propiedad initialLocation
    if (changes['initialLocation'] && !changes['initialLocation'].firstChange && this.map) {
      // console.log('Cambio detectado en initialLocation:', changes['initialLocation'].currentValue);
      // Actualizar la ubicación en el mapa
      this.showLocation(changes['initialLocation'].currentValue);
      this.cdr.detectChanges();
    }
  }

  private initializeMap(): void {
    if (!this.mapContainer) return;

    const mapElement = this.mapContainer.nativeElement;

    this.mapsService.initMap(mapElement, {
      center: this.initialLocation,
      zoom: 15
    }).subscribe(map => {
      this.map = map;
      this.mapLoaded = true;
      this.cdr.detectChanges();

      // Mostrar la ubicación inicial con un marcador
      this.showLocation(this.initialLocation);

      // Añadir evento de clic en el mapa para cambiar la ubicación del marcador
      if (this.map) {
        this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const location = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng()
            };
            this.showLocation(location);
            this.getAddressFromLocation(location);
          }
        });
      }
    });
  }

  /**
   * Muestra una ubicación en el mapa
   */
  showLocation(location: { lat: number, lng: number }, title?: string): void {
    if (!this.map) return;

    // Usar el servicio para mostrar la ubicación con un marcador arrastrable
    this.mapsService.showLocation(location, title, this.draggableMarker);

    // Obtener el marcador del servicio y añadir evento de arrastre
    if (this.draggableMarker) {
      // Necesitamos acceder al marcador, así que lo creamos localmente
      if (this.marker) {
        this.marker.setMap(null);
      }

      this.marker = new google.maps.Marker({
        position: location,
        map: this.map,
        title: title || 'Ubicación seleccionada',
        animation: google.maps.Animation.DROP,
        draggable: true
      });

      // Añadir evento para cuando se termina de arrastrar el marcador
      this.marker.addListener('dragend', () => {
        if (this.marker && this.marker.getPosition()) {
          const position = this.marker.getPosition();
          const newLocation = {
            lat: position!.lat(),
            lng: position!.lng()
          };

          // Obtener la dirección de la nueva ubicación
          this.getAddressFromLocation(newLocation);
        }
      });
    } else {
      this.mapsService.showLocation(location, title, false);
    }
  }

  /**
   * Obtiene la dirección a partir de coordenadas y emite el evento
   */
  private getAddressFromLocation(location: { lat: number, lng: number }): void {
    this.mapsService.getAddressFromCoordinates(location).subscribe(result => {
      if (result) {
        // Emitir evento con la ubicación y dirección
        this.locationChanged.emit({
          location: result.location,
          address: result.address
        });
        // console.log('Dirección obtenida:', result.address);
      }
    });
  }
}